import { makeAutoObservable, runInAction } from "mobx";
import { UserModel } from "models/UserModel";
import {
  clearStoredToken,
  coreapi,
  CoreAPIError,
  User,
} from "services/coreapi";

/**
 * The only analytics capability auth needs. Each app's `Tracking` class
 * satisfies this structurally, so core stays independent of app-specific
 * event unions and PostHog keys.
 */
export interface AuthTracking {
  identify(userID: string, email: string): void;
  /** Forget the identified user (sign-out), so the next one isn't merged in. */
  reset?(): void;
}

/**
 * Builds the app's user model from the fetched user. Apps pass a factory
 * returning their own UserModel subclass to get brand-specific behavior
 * and typed custom properties.
 */
export type CreateUserModel = (user: User) => UserModel;

export class AuthStore {
  user: UserModel | null = null;
  isInitializing = true;
  /**
   * The last `init()` could not find out whether there is a session
   * (offline, server error). Distinct from "no session": the provider shows
   * a retry instead of sending a possibly signed-in user to the login page.
   */
  initError: Error | null = null;
  /** In-flight `init()` — StrictMode and the callback effect share one. */
  private initialization: Promise<void> | null = null;
  /** In-flight `revalidateSession` — a burst of 401s shares one check. */
  private revalidation: Promise<void> | null = null;

  constructor(
    private tracking: AuthTracking,
    private ageConsentVersion: string,
    private createUserModel: CreateUserModel = (user) => new UserModel(user),
  ) {
    makeAutoObservable<this, "revalidation" | "initialization">(this, {
      revalidation: false,
      initialization: false,
    });
  }

  /**
   * Find out who is signed in. Concurrent calls share one round of
   * requests. A definite "no session" clears the user and the stored token;
   * a failure to check (offline, 5xx, a proxy's 403) keeps both and sets
   * `initError`.
   */
  init(): Promise<void> {
    if (this.initialization) return this.initialization;

    const run = (async () => {
      runInAction(() => {
        this.isInitializing = true;
        this.initError = null;
      });

      try {
        console.log("AuthStore: Initializing authentication state");
        const authSession = await coreapi.getAuthSession();

        if (!authSession) {
          // A dead token would otherwise ride along on every call.
          clearStoredToken();
          runInAction(() => {
            this.user = null;
          });
          return;
        }

        const user = await coreapi.getMe();

        if (user.email) {
          this.tracking.identify(user.id, user.email);
        }

        runInAction(() => {
          // Keep the model instance stable across repeat inits (StrictMode
          // double-runs the mount effect): consumers like an app RootStore
          // capture the instance once, so replacing it would leave them
          // observing an orphaned model that no longer receives updates.
          if (this.user && this.user.data.id === user.id) {
            this.user.data = user;
          } else {
            this.user = this.createUserModel(user);
          }
        });
      } catch (err) {
        console.error("AuthStore: could not initialise the session", err);
        runInAction(() => {
          this.initError = err instanceof Error ? err : new Error(String(err));
        });
      } finally {
        runInAction(() => {
          this.isInitializing = false;
        });
        this.initialization = null;
      }
    })();

    this.initialization = run;
    return run;
  }

  /**
   * An API rejected the bearer token (401). Ask the core API whether the
   * session still exists: if it's gone, drop the user and the token so
   * `RouteGuard` sends them to login instead of every screen failing to
   * load. If the check itself fails (offline, server down) nothing
   * changes — that isn't evidence of a logout.
   */
  revalidateSession = (): Promise<void> => {
    if (this.revalidation) return this.revalidation;

    const check = (async () => {
      try {
        const hasSession = await coreapi.hasAuthSession();
        if (hasSession) return;

        console.warn("AuthStore: session is gone, signing out locally");
        clearStoredToken();
        runInAction(() => {
          this.user = null;
        });
      } catch (err) {
        console.error("AuthStore: could not revalidate session", err);
      } finally {
        this.revalidation = null;
      }
    })();

    this.revalidation = check;
    return check;
  };

  loginWithPassword = async (input: { email: string; password: string }) => {
    await coreapi.loginWithPassword(input);
    await this.init();
  };

  /**
   * Rejects when the link could not be sent (offline, rate-limited, server
   * error) — the form must not claim "sent" then. Resolves for unknown
   * emails too: the server answers the same either way, on purpose.
   */
  sendMagicLink = async (input: { email: string; callbackURL?: string }) => {
    await coreapi.sendMagicLink({ ...input });
  };

  /**
   * Sign out. The local token is dropped whatever the server says (see
   * `coreapi.logout`), so the user is cleared either way — keeping a
   * signed-in UI without a token would only produce 401s.
   */
  logout = async () => {
    try {
      await coreapi.logout();
    } catch (err) {
      console.error(err);
    } finally {
      this.tracking.reset?.();
      runInAction(() => {
        this.user = null;
      });
    }
  };

  cancelSubscription = async (input?: { cancelReason?: string }) => {
    if (!this.user) {
      throw new Error("Cannot cancel subscription: user is not authenticated");
    }

    await coreapi.cancelSubscription(input);
    // Refresh in place (instead of swapping the model) so app stores holding
    // this model instance observe the new subscription state.
    await this.user.refresh();
  };

  postponeNextBill = async (input: { days: number }) => {
    if (!this.user) {
      throw new Error("Cannot postpone next bill: user is not authenticated");
    }

    await coreapi.postponeNextBill(input.days);
    // Refresh in place (instead of swapping the model) so app stores holding
    // this model instance observe the new subscription state.
    await this.user.refresh();
  };

  get isAuthenticated(): boolean {
    return Boolean(this.user);
  }

  get hasActiveSubscription(): boolean {
    return !!this.user && this.user.hasActiveSubscription;
  }

  get hasCompletedOnboarding(): boolean {
    return !!this.user && this.user.hasCompletedOnboarding;
  }

  get hasGrantedAgeConsent(): boolean {
    return !!this.user && this.user.hasGrantedAgeConsent;
  }

  get hasPassword(): boolean {
    return !!this.user && this.user.hasPassword;
  }

  /**
   * Save the user's first password. `"already-set"`: the server held one
   * before this call (a lost reply, another tab), maybe not this one.
   */
  setPassword = async (
    newPassword: string,
  ): Promise<"saved" | "already-set"> => {
    // Held across the awaits: a logout mid-save nulls `this.user`.
    const user = this.user;
    if (!user) {
      throw new Error("Cannot set password: user is not authenticated");
    }

    let result: "saved" | "already-set" = "saved";
    try {
      await coreapi.setPassword({ newPassword });
    } catch (err) {
      const alreadySet =
        err instanceof CoreAPIError && err.code === "PASSWORD_ALREADY_SET";
      if (!alreadySet) {
        throw err;
      }
      result = "already-set";
    }

    // The password is saved, so a failed refresh must not report a failed
    // save: the user would retry with a different one and never know.
    user.markPasswordSet();
    try {
      // In place, so app stores holding this model observe the new state.
      await user.refresh();
    } catch (err) {
      console.error("Failed to refresh the user after setting a password:", err);
    }
    return result;
  };

  grantAgeConsent = async () => {
    if (!this.user) {
      throw new Error("Cannot grant age consent: user is not authenticated");
    }

    await coreapi.grantAgeConsent({
      userId: this.user.data.id,
      consentVersion: this.ageConsentVersion,
    });

    // Refresh in place (instead of swapping the model) so app stores holding
    // this model instance observe the new state.
    await this.user.refresh();
  };
}
